

































































































































import { Breadcrumb } from '@/components/BreadCrumbs.vue';
import ContentWrapper from '@/components/ContentWrapper.vue';
import {
  PermissionsMatrixActionsEnum,
  ResourceExceptions
} from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { Vue, Component, Watch } from 'vue-property-decorator';
import { isUserAllowed } from '@/utils/rbac.util';
import { RootState } from '@/store/store';
import { Action, State } from 'vuex-class';
import { GroupDetail } from '@/store/modules/admin/types/admin.types';
import ellipsize from 'ellipsize';
import { ToastProgrammatic as Toast } from 'buefy';
import GroupLevelAttributeView from '@/views/AdminGroupSettings/components/GroupLevelAttributes/GroupLevelAttributeView.vue';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import CourseModulesTable from './components/CourseModulesTable.vue';
import { CourseStatus, CourseVisibility } from '@/jbi-shared/types/jtms.types';
import PricingPackagesTable from './components/PricingPackagesTable.vue';
import {
  CourseDetails,
  UpdateCourseMasterDTO,
  UpdateCourseStatusDTO
} from '@/jbi-shared/dto/jtms.dtos';
import { EntityTypes } from '@/store/modules/module-tree/enums/module-tree.enums';
import UnpublishCourseModal from './components/UnpublishCourseModal.vue';
import { ApiAxiosError, ApiState } from '@/store/types/general.types';
import PublishDraftCourseModal from '../EditCourses/components/PublishDraftCourseModal.vue';
import { deepCopy } from '@/jbi-shared/util/deep-copy.util';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { CourseTabsNames } from '@/store/modules/jtms/course/course.types';
import BaseButton from '@/jbi-shared/vue-components/BaseButton.vue';

@Component({
  components: {
    BaseButton,
    ContentWrapper,
    GroupLevelAttributeView,
    CourseModulesTable,
    PricingPackagesTable
  }
})
export default class ViewCourses extends Vue {
  groupId: number = 0;
  membersCount: number = 0;
  licenseId: number = 0;
  registrationOpenTag: boolean = false;
  registrationApprovalRequiredTag: boolean = false;
  courseStatusPayload: UpdateCourseStatusDTO | null = null;
  initialRegistrationStatus: CourseStatus = CourseStatus.DRAFT;

  @Action('course/getCourseDetails')
  getCourseDetails!: (courseId: number) => void;

  @Action('admin/getGroupDetail')
  getGroupDetail!: (params: { id: number; isMembers: boolean }) => void;

  @Action('rolesAndPermissions/getGroupExceptions')
  getGroupExceptions!: (groupId: number) => Promise<ResourceExceptions>;

  @Action('course/updateCourse')
  updateCourse!: (payload: UpdateCourseMasterDTO) => void;

  @Action('courseModule/clearDeletedCourseModules')
  clearDeletedCourseModules!: () => void;

  @Action('course/setCurrentActiveTab')
  setCurrentActiveTab!: (tabName: string) => void;

  @State((state: RootState) => state.rolesAndPermissions.groupExceptions)
  public groupExceptions!: ResourceExceptions;

  @State((state: RootState) => state.admin.groupDetail)
  public groupDetail!: GroupDetail;

  @State((state: RootState) => state.admin.groupLevelAttributeValuesByGroupId)
  currentGroupLevelAttributes!: GroupLevelAttributeValue[];

  @State((state: RootState) => state.course.courseDetails)
  public courseDetails!: CourseDetails;

  @State((state: RootState) => state.course.courseCreationState.hasPackages)
  hasCoursePackages!: boolean;

  @State((state: RootState) => state.course.courseCreationState.hasModules)
  hasCourseModules!: boolean;

  @State(({ admin }: RootState) => admin.apiState.getGroupDetail.success)
  getGroupDetailsSuccess!: boolean;

  @State(({ course }: RootState) => course.apiState.getCourseDetails.success)
  getCourseDetailsSuccess!: boolean;

  @State(({ course }: RootState) => course.apiState.updateCourse)
  updateCourseApiState!: ApiState;

  // Placeholder method, to be updated for course-specific use cases
  public isUserAllowed(
    action: PermissionsMatrixActionsEnum,
    module: string,
    skipImplicitCheck?: boolean
  ): boolean {
    const groupTypeName = this.groupDetail
      ? this.groupDetail.types[0]
      : undefined;
    return isUserAllowed(
      action,
      module,
      EntityTypes.GROUP,
      groupTypeName,
      this.groupId,
      this.groupExceptions,
      skipImplicitCheck
    );
  }

  get groupProductName() {
    const name = this.groupDetail?.license?.groupProduct?.name;
    return name ? ellipsize(name, 60) : name;
  }

  get licenseSeats() {
    return this.groupDetail?.license?.seats;
  }

  get licenseStatus() {
    return this.groupDetail?.license?.isActive;
  }

  get permissionMatrixActionsEnum() {
    return PermissionsMatrixActionsEnum;
  }

  get courseId() {
    return +this.$route.params.courseId;
  }

  get fromSettings() {
    return Boolean(this.$route.params.fromSettings);
  }

  get shouldRefetchDetails() {
    return Boolean(this.$route.params.shouldRefetchDetails);
  }

  get shouldEnableEdit() {
    return this.isDraftCourse || this.isCancelledCourse;
  }

  get isDraftCourse() {
    return this.courseDetails.status === CourseStatus.DRAFT;
  }

  get isPublishedCourse() {
    return this.courseDetails.status === CourseStatus.PUBLISHED;
  }

  get isCancelledCourse() {
    return this.courseDetails.status === CourseStatus.CANCELLED;
  }

  get isUserAllowedToViewBasicDetails() {
    return this.isUserAllowed(
      PermissionsMatrixActionsEnum.READ,
      // TODO: Update with appropriate permissions
      'group_administration-groups-read_groups-read_basic_details',
      true
    );
  }

  get breadcrumbs(): Breadcrumb[] {
    const root: Breadcrumb = {
      route: {
        name: 'jtms-dashboard'
      },
      text: 'Dashboard'
    };
    const current: Breadcrumb = {
      text: this.groupDetail?.name!
    };
    return [root, current];
  }

  mounted() {
    this.courseStatusPayload = null;
    // Clear array from state as safeguard
    this.clearDeletedCourseModules();
  }

  openRegistrationSettings() {
    this.$buefy.modal.open({
      parent: this,
      component: PublishDraftCourseModal,
      hasModalCard: true, // added to prevent breaking on mobile
      trapFocus: true,
      fullScreen: false,
      canCancel: true,
      props: {
        visibility: this.courseDetails.visibility,
        openForRegistration: this.courseDetails.openForRegistration,
        requireRegistrationConfirmation: this.courseDetails
          .requireRegistrationConfirmation,
        groupRegistration: this.courseDetails.groupRegistration,
        enableQuestionnaire: this.courseDetails.hasQuestionnaire,
        freeCourse: this.courseDetails.isFreeCourse,
        isSettings: true,
        groupDetails: this.groupDetail,
        attributes: deepCopy(this.currentGroupLevelAttributes),
        isRegistrationSettings: true,
        courseStatus: this.courseDetails.status
      },
      events: {
        publishDraftCourse: (payload: UpdateCourseStatusDTO) => {
          this.handleCoursePublication(payload);
        },
        cancelCourse: (payload: UpdateCourseStatusDTO) => {
          this.handleCoursePublication(payload);
        }
      }
    });
  }

  handleCoursePublication(payload: UpdateCourseStatusDTO) {
    this.courseStatusPayload = payload;

    const updatePayload: UpdateCourseMasterDTO & { courseId: number } = {
      courseId: this.courseDetails.id,
      updateCourseStatusPayload: this.courseStatusPayload ?? undefined
    };

    this.updateCourse(updatePayload);
  }

  openUnpublishCourseModal() {
    this.$buefy.modal.open({
      parent: this,
      component: UnpublishCourseModal,
      hasModalCard: true, // added to prevent breaking on mobile
      trapFocus: true,
      fullScreen: false,
      canCancel: true,
      props: {
        isUnpublish: this.isPublishedCourse
      },
      events: {
        unpublishCourse: () => {
          // Revert to base Draft states
          this.courseStatusPayload = {
            status: CourseStatus.DRAFT,
            visibility: CourseVisibility.PRIVATE,
            openForRegistration: false,
            requireRegistrationConfirmation: false,
            isFreeCourse: false,
            hasQuestionnaire: false,
            groupRegistration: false
          };

          const updatePayload: UpdateCourseMasterDTO & { courseId: number } = {
            courseId: this.courseDetails.id,
            updateCourseStatusPayload: this.courseStatusPayload ?? undefined
          };

          this.updateCourse(updatePayload);
        },
        republishCourse: () => {
          this.courseStatusPayload = {
            status: CourseStatus.PUBLISHED,
            visibility: CourseVisibility.PUBLIC,
            openForRegistration: false,
            requireRegistrationConfirmation: false,
            isFreeCourse: false,
            hasQuestionnaire: false,
            groupRegistration: false
          };

          const updatePayload: UpdateCourseMasterDTO & { courseId: number } = {
            courseId: this.courseDetails.id,
            updateCourseStatusPayload: this.courseStatusPayload ?? undefined
          };

          this.updateCourse(updatePayload);
        }
      }
    });
  }

  handleRedirectToSettings() {
    if (this.initialRegistrationStatus === CourseStatus.CANCELLED) {
      // Revert to base Draft states
      this.courseStatusPayload = {
        status: CourseStatus.DRAFT,
        visibility: CourseVisibility.PRIVATE,
        openForRegistration: false,
        requireRegistrationConfirmation: false,
        isFreeCourse: false,
        hasQuestionnaire: false,
        groupRegistration: false
      };

      const updatePayload: UpdateCourseMasterDTO & { courseId: number } = {
        courseId: this.courseDetails.id,
        updateCourseStatusPayload: this.courseStatusPayload ?? undefined
      };

      this.updateCourse(updatePayload);
    }

    this.$router.push({
      name: 'course-settings',
      params: {
        courseId: this.courseId.toString()
      }
    });
  }

  /**
   * This watcher is specifically for handling course view when routing from the individual
   * courses in the list view.
   * For scenarios when course is updated from course settings, the `onCourseUpdateSuccess`
   * watcher will handle the refetch.
   *
   * The `fromSettings` check is meant to segregate the two scenarios and prevent duplicate
   * API calls to fetch course details.
   */
  @Watch('courseId', { immediate: true })
  onCourseIdChanged() {
    // Resets default active tab on redirection
    this.setCurrentActiveTab(CourseTabsNames.ATTRIBUTES);
    if (!this.fromSettings || this.shouldRefetchDetails) {
      this.getCourseDetails(this.courseId);
    }
  }

  @Watch('updateCourseApiState', { deep: true })
  async onCourseUpdateSuccess(state: ApiState) {
    if (state.success) {
      if (this.initialRegistrationStatus !== CourseStatus.CANCELLED) {
        Toast.open({
          queue: true,
          type: 'is-dark',
          position: 'is-top',
          message: `Course updated successfully.`
        });
      }
      this.getCourseDetails(this.courseId);
    }

    if (state.error) {
      Toast.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: String(
          (state.error as ApiAxiosError)?.response?.data?.message ?? state.error
        )
      });
    }
  }

  @isTruthy
  @Watch('getCourseDetailsSuccess', { immediate: true })
  onGetCourseDetailsSuccess() {
    this.initialRegistrationStatus = this.courseDetails.status;
    this.groupId = this.courseDetails.relatedJAASGroupId;
    this.getGroupDetail({ id: this.groupId, isMembers: false });
    this.getGroupExceptions(this.groupId);
  }

  @isTruthy
  @Watch('getGroupDetailsSuccess', { immediate: true })
  onGetGroupDetailsSuccess() {
    this.membersCount = this.groupDetail.members?.length!;
    this.licenseId = +this.groupDetail.license?.id;
  }
}
